import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Group,
  Loader,
  Notification,
  Paper,
  Stack,
  Text,
  Title,
  ThemeIcon,
  Badge,
  Tabs,
  Center,
  Table,
  ScrollArea,
  SimpleGrid,
  TextInput,
  Select,
  Card,
  Divider,
  rem,
  Pagination,
  UnstyledButton,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconArrowLeft,
  IconCar,
  IconCheck,
  IconDownload,
  IconFilter,
  IconUsers,
  IconX,
  IconFileTypePdf,
  IconClipboardText,
  IconMapPin,
  IconTruckDelivery,
  IconSelector,
  IconChevronUp,
  IconChevronDown,
  IconFileText,
  IconClockPlay,
} from '@tabler/icons-react';
import api from '../../api/axiosConfig';

const heroGradient =
  'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 35%, rgba(37,99,235,0.88) 100%)';

const Reports = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('vehicle');
  const [vehicleLogs, setVehicleLogs] = useState([]);
  const [guestLogs, setGuestLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination & Sorting State
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Filter States
  const [vehicleFilters, setVehicleFilters] = useState({ dateRange: [null, null], plate: '', driverName: '', status: '' });
  const [guestFilters, setGuestFilters] = useState({ dateRange: [null, null], visitorName: '', status: '' });

  const [alertInfo, setAlertInfo] = useState({ show: false, type: '', message: '' });

  useEffect(() => {
    if (activeTab === 'vehicle') {
      fetchVehicleReports();
    } else if (activeTab === 'guest') {
      fetchGuestReports();
    }
    setPage(1); // Sekme değiştiğinde 1. sayfaya dön
  }, [activeTab]);

  const showCustomAlert = (type, message) => {
    setAlertInfo({ show: true, type, message });
    if (type === 'success') {
      setTimeout(() => setAlertInfo({ show: false, type: '', message: '' }), 2000);
    }
  };

  const formatDateForApi = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const buildQueryParams = (filters) => {
    const params = new URLSearchParams();
    if (filters.dateRange?.[0]) params.append('start_date', formatDateForApi(filters.dateRange[0]));
    if (filters.dateRange?.[1]) params.append('end_date', formatDateForApi(filters.dateRange[1]));
    
    if (filters.plate) params.append('plate', filters.plate);
    if (filters.driverName) params.append('driver_name', filters.driverName);
    
    if (filters.visitorName) params.append('visitor_name', filters.visitorName);
    if (filters.status) params.append('status', filters.status);
    return params.toString();
  };

  const fetchVehicleReports = async () => {
    setLoading(true);
    try {
      const query = buildQueryParams(vehicleFilters);
      const res = await api.get(`/office-operations/reports/vehicles?${query}`);
      setVehicleLogs(res.data || []);
      setPage(1);
    } catch (error) {
      showCustomAlert('error', error.response?.data?.detail || 'Araç rapor verisi alınamadı.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGuestReports = async () => {
    setLoading(true);
    try {
      const query = buildQueryParams(guestFilters);
      const res = await api.get(`/office-operations/reports/guests?${query}`);
      setGuestLogs(res.data || []);
      setPage(1);
    } catch (error) {
      showCustomAlert('error', error.response?.data?.detail || 'Misafir rapor verisi alınamadı.');
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const query = activeTab === 'vehicle' ? buildQueryParams(vehicleFilters) : buildQueryParams(guestFilters);
      const endpoint = activeTab === 'vehicle' 
        ? `/office-operations/reports/vehicles/export?${query}`
        : `/office-operations/reports/guests/export?${query}`;

      const fileName = activeTab === 'vehicle' ? 'arac_raporu.xlsx' : 'misafir_raporu.xlsx';

      const response = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showCustomAlert('success', 'Excel dosyası başarıyla indirildi.');
    } catch (error) {
      showCustomAlert('error', 'Excel dosyası indirilirken hata oluştu.');
    }
  };

  const downloadPdf = async () => {
    try {
      const query = activeTab === 'vehicle' ? buildQueryParams(vehicleFilters) : buildQueryParams(guestFilters);
      const endpoint = activeTab === 'vehicle' 
        ? `/office-operations/reports/vehicles/export-pdf?${query}`
        : `/office-operations/reports/guests/export-pdf?${query}`;

      const fileName = activeTab === 'vehicle' ? 'arac_takip_cizelgesi.pdf' : 'misafir_raporu.pdf';

      const response = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showCustomAlert('success', 'PDF Raporu başarıyla indirildi.');
    } catch (error) {
      showCustomAlert('error', 'PDF dosyası indirilirken hata oluştu.');
    }
  };

  const clearFilters = () => {
    if (activeTab === 'vehicle') {
      setVehicleFilters({ dateRange: [null, null], plate: '', driverName: '', status: '' });
      setTimeout(fetchVehicleReports, 100);
    } else {
      setGuestFilters({ dateRange: [null, null], visitorName: '', status: '' });
      setTimeout(fetchGuestReports, 100);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const calculateDuration = (start, end, activeText = 'Devam Ediyor') => {
    if (!start) return '-';
    if (!end) return activeText;
    const diff = new Date(end) - new Date(start);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}sa ${minutes}dk`;
  };

  // --- STATİSTİKLER ---
  const currentData = activeTab === 'vehicle' ? vehicleLogs : guestLogs;
  
  const stats = useMemo(() => {
    if (activeTab === 'vehicle') {
      const active = vehicleLogs.filter(l => !l.return_time).length;
      return { total: vehicleLogs.length, active, completed: vehicleLogs.length - active, label1: 'Görevde', label2: 'Tamamlandı' };
    } else {
      const active = guestLogs.filter(l => !l.exit_time).length;
      return { total: guestLogs.length, active, completed: guestLogs.length - active, label1: 'İçeride', label2: 'Ayrıldı' };
    }
  }, [vehicleLogs, guestLogs, activeTab]);

  // --- PAGINATION & SORTING MANTIĞI ---
  const totalPages = Math.max(1, Math.ceil(currentData.length / pageSize));
  
  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return currentData.slice(startIndex, startIndex + pageSize);
  }, [currentData, page, pageSize]);

  // --- TABLO SATIRLARI (Performans için useMemo) ---
  const vehicleRows = useMemo(() => paginatedData.map((log) => (
    <Table.Tr key={log.id}>
      <Table.Td fw={600}>
        <Group gap="xs">
          <IconCar size={16} color="gray" />
          {log.vehicle?.plate || '-'}
        </Group>
      </Table.Td>
      <Table.Td>{log.vehicle?.model || '-'}</Table.Td>
      <Table.Td>{log.driver_name || '-'}</Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={1}>{log.reason || '-'}</Text>
      </Table.Td>
      <Table.Td>{formatDate(log.out_time)}</Table.Td>
      <Table.Td>{formatDate(log.return_time)}</Table.Td>
      <Table.Td fw={600}>{calculateDuration(log.out_time, log.return_time, 'Görevde')}</Table.Td>
      <Table.Td>
        {log.return_time ? <Badge color="teal" variant="light">Tamamlandı</Badge> : <Badge color="yellow" variant="light">Görevde</Badge>}
      </Table.Td>
    </Table.Tr>
  )), [paginatedData]);

  const guestRows = useMemo(() => paginatedData.map((log) => (
    <Table.Tr key={log.id}>
      <Table.Td fw={600}>
        <Group gap="xs">
          <IconUsers size={16} color="gray" />
          {log.visitor_name || '-'}
        </Group>
      </Table.Td>
      <Table.Td>{log.host_name || '-'}</Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={1}>{log.reason || '-'}</Text>
      </Table.Td>
      <Table.Td>{formatDate(log.entry_time)}</Table.Td>
      <Table.Td>{formatDate(log.exit_time)}</Table.Td>
      <Table.Td fw={600}>{calculateDuration(log.entry_time, log.exit_time, 'İçeride')}</Table.Td>
      <Table.Td>
        {log.exit_time ? <Badge color="teal" variant="light">Ayrıldı</Badge> : <Badge color="blue" variant="light">İçeride</Badge>}
      </Table.Td>
    </Table.Tr>
  )), [paginatedData]);

  return (
    <Container fluid p={{ base: 'sm', md: 'lg' }}>
      <Stack gap="xl">
        
        {/* HERO SECTION */}
        <Paper
          radius="xl"
          p={{ base: 'lg', md: 'xl' }}
          style={{
            background: heroGradient,
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
          }}
        >
          <Stack gap="md" style={{ position: 'relative', zIndex: 2 }}>
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <Button
                variant="white"
                radius="xl"
                size="sm"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate('/office-operations/dashboard')}
                style={{
                  background: 'rgba(255,255,255,0.14)',
                  color: 'white',
                  border: 'none',
                }}
              >
                Panele Dön
              </Button>

              <Group gap="xs">
                <Badge variant="light" color="cyan" radius="xl">
                  {stats.total} Kayıt
                </Badge>
              </Group>
            </Group>

            <Group gap="sm" align="center">
              <ThemeIcon size={42} radius="xl" variant="white" color="blue">
                <IconClipboardText size={22} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text size="sm" fw={600} c="rgba(255,255,255,0.8)">
                  Ofis & Güvenlik Yönetimi
                </Text>
                <Title order={1} fw={900} c="white">
                  Sistem Raporları
                </Title>
              </Stack>
            </Group>

            <Text c="rgba(255,255,255,0.78)" size="sm" maw={800}>
              Araç ve misafir hareketlerini inceleyin, filtreleyin ve resmi çizelgeler halinde PDF / Excel formatında dışa aktarın.
            </Text>
          </Stack>
        </Paper>

        {alertInfo.show && (
          <Notification radius="md" withBorder color={alertInfo.type === 'success' ? 'teal' : 'red'} onClose={() => setAlertInfo({ show: false, type: '', message: '' })}>
            {alertInfo.message}
          </Notification>
        )}

        {/* TAB MENÜSÜ */}
        <Tabs value={activeTab} onChange={setActiveTab} radius="md" variant="pills" color="blue">
          <Tabs.List mb="md">
            <Tabs.Tab value="vehicle" leftSection={<IconCar size={16} />}>Araç Hareketleri</Tabs.Tab>
            <Tabs.Tab value="guest" leftSection={<IconUsers size={16} />}>Misafir Kayıtları</Tabs.Tab>
          </Tabs.List>

          {/* İSTATİSTİK KARTLARI */}
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <Paper radius="xl" p="lg" withBorder>
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text size="sm" c="dimmed">Toplam Kayıt</Text>
                  <Text fw={900} size={rem(28)}>{stats.total}</Text>
                </Stack>
                <ThemeIcon radius="xl" variant="light" color="blue" size={42}>
                  <IconClipboardText size={20} />
                </ThemeIcon>
              </Group>
            </Paper>

            <Paper radius="xl" p="lg" withBorder>
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text size="sm" c="dimmed">{stats.label1}</Text>
                  <Text fw={900} size={rem(28)}>{stats.active}</Text>
                </Stack>
                <ThemeIcon radius="xl" variant="light" color={activeTab === 'vehicle' ? 'yellow' : 'cyan'} size={42}>
                  <IconClockPlay size={20} />
                </ThemeIcon>
              </Group>
            </Paper>

            <Paper radius="xl" p="lg" withBorder>
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text size="sm" c="dimmed">{stats.label2}</Text>
                  <Text fw={900} size={rem(28)}>{stats.completed}</Text>
                </Stack>
                <ThemeIcon radius="xl" variant="light" color="teal" size={42}>
                  <IconCheck size={20} />
                </ThemeIcon>
              </Group>
            </Paper>
          </SimpleGrid>

          {/* FİLTRE & TABLO ALANI */}
          <Paper radius="xl" p={{ base: 'md', md: 'xl' }} withBorder shadow="sm" mt="xl">
            <Stack gap="lg">
              <Group justify="space-between" align="flex-end" wrap="wrap">
                <Stack gap={4} style={{ flex: 1, minWidth: 240 }}>
                  <Group gap="xs">
                    <IconFilter size={18} />
                    <Text fw={700}>Detaylı Filtreler</Text>
                  </Group>
                  <Text size="sm" c="dimmed">Tarih aralığı, isim, plaka ve duruma göre verileri süz.</Text>
                </Stack>

                <Group gap="sm" wrap="wrap">
                  <Button leftSection={<IconX size={16} />} radius="xl" variant="light" color="gray" onClick={clearFilters}>
                    Filtreleri Temizle
                  </Button>
                  <Button leftSection={<IconDownload size={18} />} radius="xl" color="green" variant="light" onClick={downloadExcel} disabled={currentData.length === 0}>
                    Excel'e Aktar
                  </Button>
                  <Button leftSection={<IconFileTypePdf size={18} />} radius="xl" color="red" variant="light" onClick={downloadPdf} disabled={currentData.length === 0}>
                    PDF İndir
                  </Button>
                </Group>
              </Group>

              {/* DİNAMİK FİLTRE İNPUTLARI */}
{/* DİNAMİK FİLTRE İNPUTLARI */}
{activeTab === 'vehicle' ? (
  <SimpleGrid cols={{ base: 1, md: 5 }} align="flex-end">
    <DatePickerInput 
      type="range" 
      label="Tarih Aralığı" 
      placeholder="Başlangıç - Bitiş" 
      value={vehicleFilters.dateRange} 
      onChange={(val) => setVehicleFilters({ ...vehicleFilters, dateRange: val })} 
      valueFormat="DD.MM.YYYY" 
      clearable 
      allowSingleDateInRange  // <-- TEK GÜN SEÇİLEBİLMESİ İÇİN EKLENDİ
      radius="md" 
    />
    <TextInput label="Plaka" placeholder="Plaka ara..." value={vehicleFilters.plate} onChange={(e) => setVehicleFilters({ ...vehicleFilters, plate: e.target.value.toUpperCase() })} radius="md" />
    <TextInput label="Sürücü Adı" placeholder="Sürücü ara..." value={vehicleFilters.driverName} onChange={(e) => setVehicleFilters({ ...vehicleFilters, driverName: e.target.value })} radius="md" />
    <Select label="Durum" placeholder="Tümü" data={[{ value: '', label: 'Tümü' }, { value: 'active', label: 'Görevde' }, { value: 'completed', label: 'Tamamlandı' }]} value={vehicleFilters.status} onChange={(val) => setVehicleFilters({ ...vehicleFilters, status: val || '' })} radius="md" />
    <Button fullWidth onClick={fetchVehicleReports} leftSection={<IconFilter size={16} />} radius="md" color="blue">Filtrele</Button>
  </SimpleGrid>
) : (
  <SimpleGrid cols={{ base: 1, md: 4 }} align="flex-end">
    <DatePickerInput 
      type="range" 
      label="Tarih Aralığı" 
      placeholder="Başlangıç - Bitiş" 
      value={guestFilters.dateRange} 
      onChange={(val) => setGuestFilters({ ...guestFilters, dateRange: val })} 
      valueFormat="DD.MM.YYYY" 
      clearable 
      allowSingleDateInRange  // <-- TEK GÜN SEÇİLEBİLMESİ İÇİN EKLENDİ
      radius="md" 
    />
    <TextInput label="Misafir Adı" placeholder="İsim ara..." value={guestFilters.visitorName} onChange={(e) => setGuestFilters({ ...guestFilters, visitorName: e.target.value })} radius="md" />
    <Select label="Durum" placeholder="Tümü" data={[{ value: '', label: 'Tümü' }, { value: 'active', label: 'İçeride' }, { value: 'completed', label: 'Ayrıldı' }]} value={guestFilters.status} onChange={(val) => setGuestFilters({ ...guestFilters, status: val || '' })} radius="md" />
    <Button fullWidth onClick={fetchGuestReports} leftSection={<IconFilter size={16} />} radius="md" color="blue">Filtrele</Button>
  </SimpleGrid>
)}

              <Divider />

              {/* TABLO BÖLÜMÜ */}
              {loading ? (
                <Center py="xl"><Loader size="lg" /></Center>
              ) : (
                <Box>
                  {currentData.length === 0 ? (
                    <Card withBorder radius="xl" p="xl">
                      <Stack align="center" gap="xs" py="lg">
                        <ThemeIcon size={52} radius="xl" variant="light" color="blue">
                          <IconFileText size={26} />
                        </ThemeIcon>
                        <Text fw={700} size="lg">Kayıt bulunamadı</Text>
                        <Text c="dimmed" ta="center" size="sm">Filtreleri temizleyip tekrar deneyin.</Text>
                      </Stack>
                    </Card>
                  ) : (
                    <>
                      <ScrollArea>
                        <Table verticalSpacing="md" highlightOnHover striped withTableBorder>
                          <Table.Thead>
                            {activeTab === 'vehicle' ? (
                              <Table.Tr>
                                <Table.Th><Text fw={700} size="sm">Plaka</Text></Table.Th>
                                <Table.Th><Text fw={700} size="sm">Araç Modeli</Text></Table.Th>
                                <Table.Th><Text fw={700} size="sm">Sürücü</Text></Table.Th>
                                <Table.Th><Text fw={700} size="sm">Neden</Text></Table.Th>
                                <Table.Th><Text fw={700} size="sm">Çıkış</Text></Table.Th>
                                <Table.Th><Text fw={700} size="sm">Dönüş</Text></Table.Th>
                                <Table.Th><Text fw={700} size="sm">Süre</Text></Table.Th>
                                <Table.Th><Text fw={700} size="sm">Durum</Text></Table.Th>
                              </Table.Tr>
                            ) : (
                              <Table.Tr>
                                <Table.Th><Text fw={700} size="sm">Misafir Adı</Text></Table.Th>
                                <Table.Th><Text fw={700} size="sm">Ziyaret Edilen</Text></Table.Th>
                                <Table.Th><Text fw={700} size="sm">Sebep / Firma</Text></Table.Th>
                                <Table.Th><Text fw={700} size="sm">Giriş Saati</Text></Table.Th>
                                <Table.Th><Text fw={700} size="sm">Çıkış Saati</Text></Table.Th>
                                <Table.Th><Text fw={700} size="sm">Süre</Text></Table.Th>
                                <Table.Th><Text fw={700} size="sm">Durum</Text></Table.Th>
                              </Table.Tr>
                            )}
                          </Table.Thead>
                          <Table.Tbody>
                            {activeTab === 'vehicle' ? vehicleRows : guestRows}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>

                      {/* PAGİNATİON ALANI */}
                      <Group justify="space-between" align="center" mt="lg" wrap="wrap">
                        <Group gap="sm" align="center">
                          <Select
                            data={[
                              { value: '10', label: '10 Kayıt' },
                              { value: '25', label: '25 Kayıt' },
                              { value: '50', label: '50 Kayıt' },
                              { value: '100', label: '100 Kayıt' },
                            ]}
                            value={String(pageSize)}
                            onChange={(val) => {
                              setPageSize(Number(val));
                              setPage(1); 
                            }}
                            allowDeselect={false} 
                            size="sm"
                            radius="xl"
                            w={120}
                          />
                          <Text size="sm" c="dimmed">
                            Sayfa {page} / {totalPages} — Toplam {currentData.length} kayıt
                          </Text>
                        </Group>

                        <Pagination
                          value={page}
                          onChange={setPage}
                          total={totalPages}
                          radius="xl"
                          withEdges
                        />
                      </Group>
                    </>
                  )}
                </Box>
              )}
            </Stack>
          </Paper>
        </Tabs>
      </Stack>
    </Container>
  );
};

export default Reports;